import { useSugarReadings7Days } from '../hooks/useSugarReadings';
import { HelpCircle } from 'lucide-react';

function getValue(r: any): number {
  return Number(r?.value ?? r?.reading_value ?? r?.reading ?? 0);
}

export function LowBloodGlucoseIndex() {
  const _r = useSugarReadings7Days();
  const result = _r != null && typeof _r === 'object' ? _r : { data: undefined, isLoading: true };
  const readingsData = result.data;
  const isLoading = result?.isLoading ?? false;

  const readings = Array.isArray(readingsData) ? readingsData : readingsData?.data ?? [];
  let lbgi = 0.2;

  if (readings.length > 0) {
    const lowReadings = readings.filter(r => getValue(r) < 70);
    if (lowReadings.length > 0) {
      const risks = lowReadings.map(r => {
        const bg = getValue(r);
        if (bg <= 0) return 0;
        const f = 1.509 * (Math.log(bg) ** 1.084 - 5.381);
        return 10 * (f ** 2);
      });
      lbgi = Number(
        (risks.reduce((sum, r) => sum + r, 0) / risks.length).toFixed(1)
      );
    }
  }

  const getColor = () => {
    if (lbgi < 1.1) return '#10b981';
    if (lbgi < 2.5) return '#fbbf24';
    if (lbgi < 5) return '#fb923c';
    return '#ef4444';
  };

  const getPosition = () => {
    const max = 5;
    return Math.min((lbgi / max) * 100, 100);
  };

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-dropsugar-green/10 to-dropsugar-blue/10 rounded-2xl p-6 border-2 border-dropsugar-green/30 animate-pulse">
        <div className="h-8 bg-white rounded w-1/2 mb-4"></div>
        <div className="h-24 bg-white rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-dropsugar-green/10 to-dropsugar-blue/10 rounded-2xl p-6 border-2 border-dropsugar-green/30">
      <div className="flex items-center gap-2 mb-6">
        <h3 className="text-xl font-bold text-gray-900">Low Blood Glucose Index (LBGI)</h3>
        <HelpCircle className="w-5 h-5 text-gray-400" />
      </div>

      <div className="bg-white rounded-xl p-6 border-2 border-dropsugar-blue/20">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-600">0</span>
            <span className="text-gray-900 font-medium">{lbgi}</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-600">1.1</span>
            <span className="text-gray-600">2.5</span>
            <span className="text-gray-600">5</span>
          </div>
        </div>

        <div className="relative h-8 rounded-full overflow-hidden mb-4">
          <div className="absolute inset-0 flex">
            <div className="flex-1 bg-gradient-to-r from-green-400 to-green-500"></div>
            <div className="flex-1 bg-gradient-to-r from-green-500 to-yellow-400"></div>
            <div className="flex-1 bg-gradient-to-r from-yellow-400 to-orange-400"></div>
            <div className="flex-1 bg-gradient-to-r from-orange-400 to-red-500"></div>
          </div>

          <div
            className="absolute top-1/2 w-4 h-12 bg-white rounded-full shadow-lg border-2 border-dropsugar-blue transition-all duration-500"
            style={{
              left: `${getPosition()}%`,
              transform: 'translate(-50%, -50%)',
            }}
          ></div>
        </div>

        <div className="flex justify-between text-xs text-gray-600">
          <span>Urgent Low</span>
          <span>Low</span>
          <span>Medium</span>
          <span>High</span>
        </div>
      </div>
    </div>
  );
}
